import React, { useCallback, useState } from 'react';

import Badge, { BadgeVariant } from '../../../../components/Badge';
import { ErrorIcon, LocationIcon, RetryErrorIcon, SuccessIcon } from '../../../../components/Icon';
import StatusStepIndicator, { StepStatus } from '../StatusStepIndicator';

import './ApplicationStatusTracker.css';

export interface StatusStep {
  /**
   * Unique identifier for the step
   */
  id: string;
  /**
   * Title of the step
   */
  title: string;
  /**
   * Status of the step
   */
  status: StepStatus;
  /**
   * Badge variant to display (determines which badge to show)
   */
  badgeVariant?: BadgeVariant;
  /**
   * Content to display when the step is expanded
   */
  content: React.ReactNode;
  /**
   * Whether this step is required (displays asterisk)
   */
  required?: boolean;
}

interface ApplicationStatusTrackerProps {
  /**
   * Array of status steps to display
   */
  steps: StatusStep[];
  /**
   * Whether all steps should be expanded by default
   */
  defaultAllExpanded?: boolean;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

/**
 * ApplicationStatusTracker component
 *
 * Displays a visual tracker for application status with expandable steps.
 * Each step shows an icon, connecting lines, and an accordion with status badge.
 *
 * @example
 * <ApplicationStatusTracker
 *   steps={[
 *     { id: '1', title: 'Step 1', status: 'completed', badgeVariant: 'complete', content: <div>Content</div> },
 *     { id: '2', title: 'Step 2', status: 'current', badgeVariant: 'current', content: <div>Content</div> }
 *   ]}
 * />
 */
export function ApplicationStatusTracker({
  steps,
  defaultAllExpanded = false,
  className = '',
  'data-testid': dataTestId,
}: ApplicationStatusTrackerProps) {
  const baseTestId = dataTestId ?? 'application-status-tracker';
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(() => {
    if (defaultAllExpanded) {
      return new Set(steps.map((step) => step.id));
    }

    return new Set();
  });

  const [allExpanded, setAllExpanded] = useState(defaultAllExpanded);

  const [announcement, setAnnouncement] = useState('');

  const toggleStep = useCallback(
    (stepId: string, stepTitle: string) => {
      setExpandedSteps((prev) => {
        const newSet = new Set(prev);

        if (newSet.has(stepId)) {
          newSet.delete(stepId);
          setAnnouncement(`${stepTitle} collapsed`);
        } else {
          newSet.add(stepId);
          setAnnouncement(`${stepTitle} expanded`);
        }

        return newSet;
      });
    },
    [setAnnouncement],
  );

  const toggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedSteps(new Set());
      setAllExpanded(false);
      setAnnouncement('All steps collapsed');
    } else {
      setExpandedSteps(new Set(steps.map((step) => step.id)));
      setAllExpanded(true);
      setAnnouncement('All steps expanded');
    }
  }, [allExpanded, steps]);

  const getStepIcon = (status: StepStatus) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon size={32} />;
      case 'current':
        return <LocationIcon size={32} />;
      case 'error':
        return <RetryErrorIcon size={32} />;
      case 'terminal':
        return <ErrorIcon size={32} />;
      case 'pending':
        return null;
      default:
        return null;
    }
  };

  const getBadgeForStep = (step: StatusStep) => {
    if (!step.badgeVariant) {
      return null;
    }

    // The attention state always reads "Action Needed" (per the eNOVA status
    // descriptions doc), whether the step is collapsed or expanded. The label
    // must stay generic because action-needed covers returned-for-updates,
    // timeouts, and denials alike.
    if (step.badgeVariant === 'action-needed') {
      return (
        <>
          <ErrorIcon size={24} />
          <Badge variant='action-needed' />
        </>
      );
    }

    return <Badge variant={step.badgeVariant} />;
  };

  return (
    <div
      className={`application-status-tracker ${className}`}
      data-testid={baseTestId}>
      <div
        aria-atomic='true'
        aria-live='polite'
        className='ds-u-visibility--screen-reader'
        data-testid={`${baseTestId}-announcement`}>
        {announcement}
      </div>
      <div
        className='application-status-tracker__controls'
        data-testid={`${baseTestId}-controls`}>
        <button
          aria-label={allExpanded ? 'Collapse all steps' : 'Expand all steps'}
          className='application-status-tracker__toggle-all'
          data-testid={`${baseTestId}-toggle-all`}
          type='button'
          onClick={toggleAll}>
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      <div
        className='application-status-tracker__steps-container'
        data-testid={`${baseTestId}-steps-container`}>
        <div
          aria-label='Application status steps'
          className='application-status-tracker__steps'
          data-testid={`${baseTestId}-steps`}
          role='list'>
          {steps.map((step, index) => {
            const isExpanded = expandedSteps.has(step.id);
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;

            return (
              <div
                key={step.id}
                className='application-status-tracker__step'
                data-testid={`${baseTestId}-step-${step.id}`}
                role='listitem'>
                <StatusStepIndicator
                  icon={getStepIcon(step.status)}
                  showLineDown={!isLast}
                  showLineUp={!isFirst}
                  status={step.status}
                />

                <div
                  className='application-status-tracker__step-content'
                  data-testid={`${baseTestId}-step-content-${step.id}`}>
                  <div
                    className='application-status-tracker__accordion'
                    data-testid={`${baseTestId}-accordion-${step.id}`}>
                    <button
                      aria-controls={`status-step-content-${step.id}`}
                      aria-expanded={isExpanded}
                      aria-label={`${step.title}${step.required ? ', required' : ''}, ${
                        isExpanded ? 'expanded' : 'collapsed'
                      }`}
                      className='application-status-tracker__accordion-header'
                      data-testid={`${baseTestId}-accordion-header-${step.id}`}
                      id={`status-step-header-${step.id}`}
                      type='button'
                      onClick={() => toggleStep(step.id, `Step ${index + 1}: ${step.title}`)}>
                      <div
                        className='application-status-tracker__accordion-title'
                        data-testid={`${baseTestId}-accordion-title-${step.id}`}>
                        {getBadgeForStep(step)}
                        <span className='application-status-tracker__step-title'>
                          Step {index + 1}: {step.title}
                          {step.required && (
                            <span
                              aria-label='required'
                              className='application-status-tracker__required'>
                              *
                            </span>
                          )}
                        </span>
                      </div>
                      <span
                        aria-hidden='true'
                        className='application-status-tracker__accordion-icon'
                        data-testid={`${baseTestId}-accordion-icon-${step.id}`}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div
                        aria-labelledby={`status-step-header-${step.id}`}
                        className='application-status-tracker__accordion-content'
                        data-testid={`${baseTestId}-accordion-content-${step.id}`}
                        id={`status-step-content-${step.id}`}
                        role='region'>
                        {step.content}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ApplicationStatusTracker;
